import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

export default function CommentsSection({ entityType, entityId, token, currentUser }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (entityType && entityId && token) {
      fetchComments();
    }
  }, [entityType, entityId, token]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/comments?entity_type=${entityType}&entity_id=${entityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      } else {
        console.error('Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/comments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            entity_type: entityType,
            entity_id: entityId,
            text: newComment
          })
        }
      );

      if (response.ok) {
        const newCommentData = await response.json();
        setComments([...comments, newCommentData]);
        setNewComment('');
        toast.success('Comment added');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Check if user has permission to add comments
  const canAddComment = () => {
    if (currentUser?.role === 'admin') return true;
    
    const permissions = currentUser?.permissions || {};
    const resourcePerms = permissions[entityType + 's'] || {}; // 'lead' -> 'leads'
    return resourcePerms.add_comments === true;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <MessageCircle className="h-4 w-4" />
        <span>Comments ({comments.length})</span>
      </div>

      {/* Comments List */}
      <div className="space-y-3 max-h-60 overflow-y-auto">
        {loading ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            No comments yet. Be the first to comment!
          </div>
        ) : (
          comments.map((comment) => (
            <div 
              key={comment._id} 
              className="bg-muted/30 rounded-lg p-3 space-y-2"
              data-testid={`comment-${comment._id}`}
            >
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {comment.user_name || comment.user_email}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatTimestamp(comment.created_at)}
                </span>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {comment.text}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      {canAddComment() && (
        <form data-testid="comments-section-form-1" onSubmit={handleSubmit} className="space-y-2">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={3}
            className="resize-none"
            data-testid="comment-input"
          />
          <div className="flex justify-end">
            <Button 
              type="submit" 
              size="sm"
              disabled={submitting || !newComment.trim()}
              className="gap-2"
              data-testid="submit-comment-button"
            >
              <Send className="h-3 w-3" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </form>
      )}

      {!canAddComment() && (
        <div className="text-xs text-muted-foreground italic">
          You don't have permission to add comments
        </div>
      )}
    </div>
  );
}
